/**
 * Bitget REST API Functions
 *
 * REST endpoint helpers for Bitget spot market data.
 * @see https://www.bitget.com/api-doc/spot/market/Get-Tickers
 */

import type {
  BitgetKlinesRequest,
  BitgetKlinesResponse,
  BitgetMergeDepthRequest,
  BitgetMergeDepthResponse,
  BitgetOrderbookRequest,
  BitgetOrderbookResponse,
  BitgetSymbolsRequest,
  BitgetSymbolsResponse,
  BitgetTickerRequest,
  BitgetTickerResponse,
  BitgetTradesRequest,
  BitgetTradesResponse,
} from './types';

import { get } from '../../lib/http';
import type { RestRequestOptions } from '../../lib/types';

const BASE_URL = 'https://api.bitget.com';

/**
 * Bitget REST API endpoints.
 * @see https://www.bitget.com/api-doc/spot/market/Get-Tickers
 */
export const bitgetRestEndpoints = {
  ticker: `${BASE_URL}/api/v2/spot/market/tickers`,
  symbols: `${BASE_URL}/api/v2/spot/public/symbols`,
  orderbook: `${BASE_URL}/api/v2/spot/market/orderbook`,
  mergeDepth: `${BASE_URL}/api/v2/spot/market/merge-depth`,
  klines: `${BASE_URL}/api/v2/spot/market/candles`,
  trades: `${BASE_URL}/api/v2/spot/market/fills`,
} as const;

/**
 * Fetches symbol information including price/quantity precision.
 * @see https://www.bitget.com/api-doc/spot/market/Get-Symbols
 */
export const getBitgetSymbols = async (
  params?: BitgetSymbolsRequest,
  options?: RestRequestOptions,
): Promise<BitgetSymbolsResponse> => {
  const response = await get<BitgetSymbolsResponse, BitgetSymbolsRequest | undefined>({
    url: bitgetRestEndpoints.symbols,
    params,
    options,
  });

  return response.data;
};

/**
 * Fetches 24-hour ticker statistics for a symbol.
 * @see https://www.bitget.com/api-doc/spot/market/Get-Tickers
 */
export const getBitgetTicker = async (
  params: BitgetTickerRequest,
  options?: RestRequestOptions,
): Promise<BitgetTickerResponse> => {
  const response = await get<BitgetTickerResponse, BitgetTickerRequest>({
    url: bitgetRestEndpoints.ticker,
    params,
    options,
  });

  return response.data;
};

/**
 * Fetches orderbook depth for a symbol.
 * @see https://www.bitget.com/api-doc/spot/market/Get-Orderbook
 */
export const getBitgetOrderbook = async (
  params: BitgetOrderbookRequest,
  options?: RestRequestOptions,
): Promise<BitgetOrderbookResponse> => {
  const response = await get<BitgetOrderbookResponse, BitgetOrderbookRequest>({
    url: bitgetRestEndpoints.orderbook,
    params,
    options,
  });

  return response.data;
};

/**
 * Fetches merge orderbook (aggregated by price scale) for a symbol.
 * @see https://www.bitget.com/api-doc/spot/market/Merge-Orderbook
 */
export const getBitgetMergeDepth = async (
  params: BitgetMergeDepthRequest,
  options?: RestRequestOptions,
): Promise<BitgetMergeDepthResponse> => {
  const response = await get<BitgetMergeDepthResponse, BitgetMergeDepthRequest>({
    url: bitgetRestEndpoints.mergeDepth,
    params,
    options,
  });

  return response.data;
};

/**
 * Fetches candlestick (kline) data for a symbol.
 * @see https://www.bitget.com/api-doc/spot/market/Get-Candles
 */
export const getBitgetKlines = async (
  params: BitgetKlinesRequest,
  options?: RestRequestOptions,
): Promise<BitgetKlinesResponse> => {
  const response = await get<BitgetKlinesResponse, BitgetKlinesRequest>({
    url: bitgetRestEndpoints.klines,
    params,
    options,
  });

  return response.data;
};

/**
 * Fetches recent trades for a symbol.
 * @see https://www.bitget.com/api-doc/spot/market/Get-Recent-Trades
 */
export const getBitgetTrades = async (
  params: BitgetTradesRequest,
  options?: RestRequestOptions,
): Promise<BitgetTradesResponse> => {
  const response = await get<BitgetTradesResponse, BitgetTradesRequest>({
    url: bitgetRestEndpoints.trades,
    params,
    options,
  });

  return response.data;
};
